// =============================================================================
// Types
// =============================================================================

export {};

/**
 * @typedef {{
 *   isAvailable?: (projectId: string, identifier: string) => Promise<boolean>;
 *   open: (opts: { projectId: string; identifier: string; height?: number }) => void;
 *   close?: () => void;
 *   onAddToCart?: (cb: (data: unknown) => void) => void;
 *   onClose?: (cb: () => void) => void;
 *   onGenerationComplete?: (cb: (data: unknown) => void) => void;
 *   onInteraction?: (cb: (data: unknown) => void) => void;
 *   onError?: (cb: (data: unknown) => void) => void;
 *   instance?: { onGenerationComplete?: (cb: (data: unknown) => void) => void };
 * }} MkSdk
 *
 * @typedef {Window & {
 *   mk?: MkSdk;
 *   dispatchVtoEvent?: (payload: { email?: string; variantId: string; event: string }) => void;
 *   dataLayer?: Record<string, unknown>[];
 * }} AppWindow
 *
 * @typedef {{ select?: (index: number, event?: Event, opts?: { animate?: boolean }) => Promise<unknown> }} SlideshowApi
 */

/** @returns {AppWindow} */
const appWindow = () => /** @type {AppWindow} */ (window);

const VTO_EMAIL_STORAGE_KEY = '_vtoUserEmail';

/**
 * Prefixo das chaves de localStorage que guardam o timestamp do último teste
 * no provador para cada variante. Ex: `_vtoTrialAt_12345678`
 */
const VTO_TRIAL_AT_PREFIX = '_vtoTrialAt_';

/**
 * @param {string | undefined} value
 * @returns {string}
 */
function normalizeString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * @returns {string}
 */
function getStoredVtoEmail() {
  try {
    return normalizeString(localStorage.getItem(VTO_EMAIL_STORAGE_KEY) ?? '');
  } catch {
    return '';
  }
}

/**
 * @param {string} value
 * @returns {boolean}
 */
function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

/**
 * Mostra um modal interno para captura de email com dimensao equivalente ao modal do VTO.
 *
 * @param {HTMLTemplateElement | null} template
 * @returns {Promise<string | null>}
 */
function openVtoEmailModal(template) {
  if (!template) return Promise.resolve(null);

  return new Promise(resolve => {
    const overlay = /** @type {HTMLElement | null} */ (
      template.content.firstElementChild?.cloneNode(true)
    );
    if (!overlay) {
      resolve(null);
      return;
    }

    document.body.appendChild(overlay);

    const form = /** @type {HTMLFormElement | null} */ (
      overlay.querySelector('.vto-email-modal__form')
    );
    const input = /** @type {HTMLInputElement | null} */ (
      overlay.querySelector('#vto-email-input')
    );
    const error = /** @type {HTMLElement | null} */ (
      overlay.querySelector('.vto-email-modal__error')
    );
    const closeBtn = /** @type {HTMLButtonElement | null} */ (
      overlay.querySelector('.vto-email-modal__close')
    );

    const cleanup = () => {
      overlay.remove();
      resolve(null);
    };

    closeBtn?.addEventListener('click', cleanup, { once: true });
    overlay.addEventListener('click', event => {
      if (event.target === overlay) cleanup();
    });

    form?.addEventListener('submit', event => {
      event.preventDefault();
      const email = normalizeString(input?.value);

      if (!isValidEmail(email)) {
        if (error) error.hidden = false;
        input?.focus();
        return;
      }

      overlay.classList.add('vto-email-modal-overlay--fade-out');
      setTimeout(() => {
        overlay.remove();
      }, 300);
      resolve(email);
    });

    requestAnimationFrame(() => input?.focus());
  });
}

// =============================================================================
// Tagging helper
// =============================================================================

/**
 * Janela de deduplicação: o SDK do provador já teve um bug conhecido onde reabrir
 * o provador para o MESMO produto na mesma sessão de página deixava um listener de
 * `message` órfão registrado (não removia o anterior antes de recriar), fazendo cada
 * evento real do iframe (interaction/generation_complete/add_to_cart) disparar 2x.
 * As duplicatas chegam sincronamente, uma logo após a outra — uma janela curta basta.
 * Mantido como proteção defensiva mesmo após a troca de SDK.
 */
const VTO_TAG_DEDUPE_WINDOW_MS = 800;

/** @type {Map<string, number>} */
const _recentVtoTags = new Map();

/**
 * @param {string} email
 * @param {string} variantId
 * @param {string} event
 */
function vtoTag(email, variantId, event) {
  const key = `${event}::${variantId}`;
  const now = Date.now();
  const last = _recentVtoTags.get(key);

  if (last !== undefined && now - last < VTO_TAG_DEDUPE_WINDOW_MS) {
    return;
  }

  _recentVtoTags.set(key, now);
  appWindow().dispatchVtoEvent?.({ email, variantId, event });
}

// =============================================================================
// Custom Element — <vto-gallery-button>
// =============================================================================

/**
 * Verifica a disponibilidade do provador virtual para a variante indicada e,
 * se disponível, exibe o botão e abre o SDK ao clique.
 *
 * Atributos observados:
 *   - data-project-id  — ID do projecto mkfashion (settings.vto_mkfashion_project_id)
 *   - data-variant-id  — Identificador da variante no SDK (normalmente o SKU)
 */
class VtoGalleryButton extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  /**
   * Sequência usada para descartar respostas assíncronas obsoletas quando o
   * elemento é actualizado enquanto um pedido anterior ainda está pendente.
   * @type {number}
   */
  #seq = 0;

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  connectedCallback() {
    this.#check();
  }

  disconnectedCallback() {
    this.#cancel();
  }

  attributeChangedCallback() {
    if (this.isConnected) this.#check();
  }

  static get observedAttributes() {
    return ['data-project-id', 'data-variant-id'];
  }

  // ---------------------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------------------

  #cancel() {
    this.#abort?.abort();
    this.#abort = undefined;
  }

  #check() {
    this.#cancel();
    this.#abort = new AbortController();
    this.classList.add('hidden');

    const seq = ++this.#seq;
    void this.#runCheck(seq, this.#abort.signal);
  }

  /**
   * @param {number} seq
   * @param {AbortSignal} signal
   */
  async #runCheck(seq, signal) {
    const projectId = this.dataset.projectId?.trim();
    const variantId = this.dataset.variantId?.trim();
    if (!projectId || !variantId) return;

    await sdkReady(signal);
    if (signal.aborted || seq !== this.#seq) return;

    const sdk = appWindow().mk;
    if (!sdk) return;

    attachGenerationCompleteListener(sdk);
    attachAddToCartListener(sdk);
    attachOnCloseListener(sdk);
    attachOnInteractionListener(sdk);
    attachOnErrorListener(sdk);

    let available = false;
    try {
      available = (await sdk.isAvailable?.(projectId, variantId)) ?? false;
    } catch (err) {
      console.warn('[vto-gallery-button] isAvailable error:', err);
    }

    if (signal.aborted || seq !== this.#seq) return;
    if (!available) return;

    this.classList.remove('hidden');
    this.querySelector('button')?.addEventListener(
      'click',
      () => {
        const win = appWindow();
        win.dataLayer = win.dataLayer || [];
        win.dataLayer.push({
          event: 'vto_button_click',
          vto_variant_id: variantId
        });

        this.#openWithEmailGuard({ sdk, projectId, variantId }).catch(err => {
          console.warn('[vto-gallery-button] open error:', err);
        });
      },
      { signal }
    );
  }

  /**
   * @param {{ sdk: MkSdk; projectId: string; variantId: string; email: string; triggerEvent: string }} params
   */
  #openSdk({ sdk, projectId, variantId, email, triggerEvent }) {
    vtoTag(email, variantId, triggerEvent);
    _activeVtoIdentifier = variantId;
    _vtoGenerationCompleted = false;
    sdk.open({ projectId, identifier: variantId });
    vtoTag(email, variantId, 'tryon_modal_opened');
  }

  /**
   * @param {{ sdk: MkSdk; projectId: string; variantId: string }} params
   */
  async #openWithEmailGuard({ sdk, projectId, variantId }) {
    const customerId = normalizeString(this.dataset.customerId);
    const customerEmail = normalizeString(this.dataset.customerEmail);
    const storedEmail = getStoredVtoEmail();
    const popupTemplate = /** @type {HTMLTemplateElement | null} */ (
      this.querySelector('template[data-vto-email-modal-template]')
    );

    if (customerId || storedEmail) {
      this.#openSdk({ sdk, projectId, variantId, email: customerEmail || storedEmail || '', triggerEvent: 'tryon_cta_clicked' });
      return;
    }
    vtoTag('', variantId, 'tryon_cta_clicked_not_logged');
    vtoTag('', variantId, 'tryon_login_prompt_shown');

    const email = await openVtoEmailModal(popupTemplate);
    if (!email) {
      vtoTag('', variantId, 'tryon_email_dismissed');
      return;
    }
    vtoTag(email, variantId, "tryon_email_submitted");
    this.#openSdk({ sdk, projectId, variantId, email, triggerEvent: 'tryon_login_completed' });
  }
}

// =============================================================================
// SDK bootstrap
// =============================================================================

/**
 * Resolve quando `window.mk` estiver disponível. O script é injetado via GTM
 * (fora do controlo do tema), então não há evento de `onload` pra escutar —
 * só polling de fallback.
 *
 * @param {AbortSignal} signal
 * @returns {Promise<void>}
 */
function sdkReady(signal) {
  if (appWindow().mk) return Promise.resolve();

  return new Promise(resolve => {
    let settled = false;

    const settle = () => {
      if (settled) return;
      settled = true;
      clearInterval(poll);
      resolve();
    };

    const poll = setInterval(() => {
      if (signal.aborted || appWindow().mk) settle();
    }, 50);

    signal.addEventListener('abort', settle);
  });
}

// =============================================================================
// Generation complete — inserção da imagem gerada na galeria
// =============================================================================

/**
 * Identifier (SKU/variantId) da última chamada a `sdk.open()`.
 * Usado como fonte primária no callback `addToCart`, onde o campo
 * `identifier` do payload do SDK pode chegar vazio.
 */
let _activeVtoIdentifier = '';

/** Garante que o listener é registado uma única vez. */
let generationListenerAttached = false;

/**
 * @param {MkSdk} sdk
 */
function attachGenerationCompleteListener(sdk) {
  if (generationListenerAttached) return;

  const register =
    typeof sdk.onGenerationComplete === 'function'
      ? sdk.onGenerationComplete.bind(sdk)
      : typeof sdk.instance?.onGenerationComplete === 'function'
        ? sdk.instance.onGenerationComplete.bind(sdk.instance)
        : null;

  if (!register) return;

  generationListenerAttached = true;
  register(onGenerationComplete);
}

/**
 * @param {unknown} rawPayload
 */
function onGenerationComplete(rawPayload) {
  const imageUrl = extractImageUrl(rawPayload);
  if (!imageUrl) return;

  _vtoGenerationCompleted = true;
  vtoTag(getStoredVtoEmail(), _activeVtoIdentifier, 'tryon_generation_completed');

  const src = toDataSrc(imageUrl);
  persistGeneratedSrcForCart(src);
  insertGeneratedImageInGallery(src);
}

// =============================================================================
// Add to cart — fecha VTO, dispara evento e abre seletor de lentes
// =============================================================================

/** Guard de idempotência para o callback addToCart — evita disparos duplicados do SDK. */
let _vtoAddToCartHandled = false;
let _vtoGenerationCompleted = false;

/** Garante que o listener addToCart é registado uma única vez. */
let addToCartListenerAttached = false;

/**
 * @param {MkSdk} sdk
 */
function attachAddToCartListener(sdk) {
  if (addToCartListenerAttached) return;

  // Seta o flag independente de addToCart já existir no SDK, evitando que
  // chamadas repetidas (ex: atributeChangedCallback antes do SDK inicializar)
  // acumulem callbacks quando o método finalmente estiver disponível.
  addToCartListenerAttached = true;

  if (typeof sdk.onAddToCart !== 'function') return;
  sdk.onAddToCart(onVtoAddToCart);
}

/**
 * Callback disparado pelo SDK quando o utilizador clica em "adicionar ao
 * carrinho" dentro do provador virtual.
 *
 * Fluxo:
 *   1. Dispara o evento `tried_added_to_cart` para rastreamento.
 *   2. Fecha o modal do VTO via `sdk.close()`.
 *   3. Abre o seletor de lentes (`lens-selection-component`).
 *
 * @param {unknown} rawPayload — payload recebido do SDK (ver docs mk-sdk-git)
 */
function onVtoAddToCart(rawPayload) {
  // Guard de idempotência: impede disparos duplicados caso o SDK chame o
  // callback mais de uma vez por ação (ex: bug de acumulação de listeners).
  if (_vtoAddToCartHandled) return;
  _vtoAddToCartHandled = true;

  const payload = /** @type {{ identifier?: string; size?: { label?: string; produtoVarianteId?: number }; product?: unknown }} */ (
    rawPayload && typeof rawPayload === 'object' ? rawPayload : {}
  );

  // `_activeVtoIdentifier` é definido imediatamente antes de sdk.open() e é a
  // fonte confiável; `payload.identifier` serve de fallback caso o SDK o forneça.
  const variantId =
    _activeVtoIdentifier ||
    normalizeString(/** @type {string | undefined} */ (payload.identifier));

  const storedEmail = getStoredVtoEmail();

  vtoTag(storedEmail, variantId, 'tryon_add_to_cart');

  appWindow().mk?.close?.();

  const lensSelector = /** @type {{ openModal?: () => void } | null} */ (
    document.querySelector('lens-selection-component')
  );
  lensSelector?.openModal?.();
}

// =============================================================================
// On close
// =============================================================================

/** Garante que o listener onClose é registado uma única vez. */
let onCloseListenerAttached = false;

/**
 * @param {MkSdk} sdk
 */
function attachOnCloseListener(sdk) {
  if (onCloseListenerAttached) return;

  onCloseListenerAttached = true;

  if (typeof sdk.onClose !== 'function') return;
  sdk.onClose(onVtoClose);
}

/** Garante que o listener onInteraction é registado uma única vez. */
let onInteractionListenerAttached = false;

/**
 * @param {MkSdk} sdk
 */
function attachOnInteractionListener(sdk) {
  if (onInteractionListenerAttached) return;

  onInteractionListenerAttached = true;

  if (typeof sdk.onInteraction !== 'function') return;
  sdk.onInteraction(data => {
    const d = /** @type {{ category?: string; action?: string; method?: string; product_id?: string }} */ (data);
    if (d.category === 'photo' && d.action === 'method_selected') {
      vtoTag(getStoredVtoEmail(), _activeVtoIdentifier, 'tryon_photo_method_selected');
    }
    if (d.category === 'photo' && d.action === 'upload') {
      vtoTag(getStoredVtoEmail(), _activeVtoIdentifier, 'tryon_photo_uploaded');
    }
  });
}

/** Garante que o listener onError é registado uma única vez. */
let onErrorListenerAttached = false;

/**
 * @param {MkSdk} sdk
 */
function attachOnErrorListener(sdk) {
  if (onErrorListenerAttached) return;

  onErrorListenerAttached = true;

  if (typeof sdk.onError !== 'function') return;
  sdk.onError(() => {
    vtoTag(getStoredVtoEmail(), _activeVtoIdentifier, 'tryon_generation_failed');
  });
}

function onVtoClose() {
  if (_vtoAddToCartHandled) {
    _vtoAddToCartHandled = false;
    return;
  }

  const variantId = _activeVtoIdentifier;
  _activeVtoIdentifier = '';
  const storedEmail = getStoredVtoEmail();

  if (_vtoGenerationCompleted) {
    _vtoGenerationCompleted = false;
    vtoTag(storedEmail, variantId, 'tryon_modal_closed');
  } else {
    vtoTag(storedEmail, variantId, 'tryon_returned_closed');
  }
}

// =============================================================================
// Payload utilities
// =============================================================================

/**
 * Extrai a URL da imagem do payload recebido do SDK (objeto ou string JSON).
 *
 * @param {unknown} raw
 * @returns {string | null}
 */
function extractImageUrl(raw) {
  if (raw == null) return null;

  const payload =
    typeof raw === 'string'
      ? (() => {
          try {
            return JSON.parse(raw);
          } catch {
            return null;
          }
        })()
      : typeof raw === 'object'
        ? raw
        : null;

  const url = /** @type {{ imageUrl?: unknown }} */ (payload)?.imageUrl;
  return typeof url === 'string' && url.length > 0 ? url : null;
}

/**
 * Normaliza a imagem: se já for `data:` usa como está; se parecer base64 puro,
 * adiciona o prefixo JPEG.
 *
 * @param {string} imageUrl
 * @returns {string}
 */
function toDataSrc(imageUrl) {
  const s = imageUrl.trim();
  if (s.startsWith('data:')) return s;
  if (/^[A-Za-z0-9+/=\s]+$/.test(s) && s.length > 100) {
    return `data:image/jpeg;base64,${s.replace(/\s/g, '')}`;
  }
  return s;
}

// =============================================================================
// Cart integration
// =============================================================================

/**
 * Persiste o timestamp do teste virtual e mantém o variantId disponível
 * para que `lens-selection.js` possa incluí-lo como propriedade da armação.
 *
 * @param {string} _src — imagem gerada (usada apenas pela galeria)
 */
function persistGeneratedSrcForCart(_src) {
  const script = document.querySelector(
    'variant-picker script[type="application/json"]'
  );

  let variantId = null;
  if (script?.textContent) {
    try {
      variantId = JSON.parse(script.textContent).id ?? null;
    } catch {
      /* ignora erros de parsing */
    }
  }

  persistVtoTrialTimestamp(variantId);
}

/**
 * Salva no localStorage o timestamp (ISO 8601) do momento em que o provador
 * virtual gerou um resultado para a variante. Sobrescreve sempre com o valor
 * mais recente.
 *
 * @param {string | number | null} variantId
 */
function persistVtoTrialTimestamp(variantId) {
  if (variantId == null) return;
  try {
    const timestamp = new Date().toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    localStorage.setItem(`${VTO_TRIAL_AT_PREFIX}${variantId}`, timestamp);
  } catch {
    /* localStorage indisponível, ignora */
  }
}

// =============================================================================
// Gallery injection
// =============================================================================

/**
 * Insere a imagem gerada na galeria de produto:
 *   - 2.º slide no carrossel (slideshow-slides), com navegação animada.
 *   - 2.ª célula no grid estático (.media-gallery__grid).
 *
 * @param {string} src
 */
function insertGeneratedImageInGallery(src) {
  const gallery = /** @type {HTMLElement | null} */ (
    document.querySelector('media-gallery')
  );
  if (!gallery) return;

  removeExistingGeneratedMedia(gallery);

  insertIntoCarousel(gallery, src);
  insertIntoGrid(gallery, src);
}

/**
 * @param {HTMLElement} root
 */
function removeExistingGeneratedMedia(root) {
  root.querySelectorAll('[data-vto-generated]').forEach(el => el.remove());
}

/**
 * Cria e insere o slide VTO no carrossel, reconstrói os controlos e navega até ele.
 *
 * @param {HTMLElement} gallery
 * @param {string} src
 */
function insertIntoCarousel(gallery, src) {
  const slideshow = /** @type {HTMLElement | null} */ (
    gallery.querySelector('slideshow-component')
  );
  const container = gallery.querySelector('slideshow-slides');
  if (!slideshow || !container) return;

  const VTO_INDEX = 1;

  container.insertBefore(
    buildSlide(src),
    container.children[VTO_INDEX] ?? null
  );

  syncTimelines(slideshow);
  rebuildDots(slideshow, VTO_INDEX);
  rebuildThumbnails(slideshow, src, VTO_INDEX);

  // Dois rAF para dar tempo ao DOM actualizar os refs do custom element antes do select.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const api = /** @type {SlideshowApi} */ (slideshow);
      api.select?.(VTO_INDEX, undefined, { animate: true })?.catch(() => {});
    });
  });

  window.dispatchEvent(new Event('resize'));
}

/**
 * Cria e insere o item VTO no grid estático de imagens.
 *
 * @param {HTMLElement} gallery
 * @param {string} src
 */
function insertIntoGrid(gallery, src) {
  const grid = gallery.querySelector('.media-gallery__grid');
  if (!grid) return;

  const li = document.createElement('li');
  li.className =
    'product-media-container product-media-container--image media-fit vto-generated-media';
  li.setAttribute('data-vto-generated', 'true');
  li.appendChild(buildMediaWrapper(src));

  grid.insertBefore(li, grid.children[1] ?? null);

  const total = grid.querySelectorAll('li').length;
  grid.setAttribute('data-media-count', String(total));
  grid.toggleAttribute('data-odd-count', total % 2 === 1);
}

// =============================================================================
// DOM builders
// =============================================================================

/**
 * @param {string} src
 * @returns {HTMLElement}
 */
function buildSlide(src) {
  const slide = document.createElement('slideshow-slide');
  slide.setAttribute('ref', 'slides[]');
  slide.setAttribute('aria-hidden', 'true');
  slide.setAttribute('data-vto-generated', 'true');
  slide.className =
    'product-media-container product-media-container--image media-fit vto-generated-media';
  slide.style.setProperty('view-timeline-name', '--slide-2');
  slide.style.setProperty('--product-media-fit', 'cover');
  slide.appendChild(buildMediaWrapper(src));
  return slide;
}

/**
 * Cria o wrapper `.product-media` com a imagem gerada dentro.
 *
 * @param {string} src
 * @returns {HTMLDivElement}
 */
function buildMediaWrapper(src) {
  const wrapper = document.createElement('div');
  wrapper.className = 'product-media';
  wrapper.style.setProperty('--ratio', '1');
  wrapper.appendChild(buildMediaImage(src));
  return wrapper;
}

/**
 * @param {string} src
 * @returns {HTMLImageElement}
 */
function buildMediaImage(src) {
  const img = document.createElement('img');
  img.className = 'product-media__image vto-generated-media__image';
  img.src = src;
  img.alt = '';
  img.loading = 'lazy';
  img.decoding = 'async';
  img.style.setProperty('object-fit', 'cover', 'important');
  img.style.setProperty('mix-blend-mode', 'normal', 'important');
  img.style.setProperty('height', '100%', 'important');
  return img;
}

// =============================================================================
// Slideshow controls sync
// =============================================================================

/**
 * Actualiza `--slideshow-timeline` e os `view-timeline-name` de cada slide.
 * Necessário após inserir/remover slides dinamicamente.
 *
 * @param {HTMLElement} slideshow
 */
function syncTimelines(slideshow) {
  const slides = slideshow.querySelectorAll('slideshow-slide');
  slideshow.style.setProperty(
    '--slideshow-timeline',
    Array.from({ length: slides.length }, (_, i) => `--slide-${i + 1}`).join(
      ', '
    )
  );
  slides.forEach((slide, i) => {
    if (slide instanceof HTMLElement) {
      slide.style.setProperty('view-timeline-name', `--slide-${i + 1}`);
      slide.style.setProperty('--product-media-fit', 'cover');
    }
  });
}

/**
 * Reconstrói a lista de dots com o número actual de slides.
 *
 * @param {HTMLElement} slideshow
 * @param {number} activeIndex
 */
function rebuildDots(slideshow, activeIndex) {
  const count = slideshow.querySelectorAll('slideshow-slide').length;

  slideshow.querySelectorAll('ol.slideshow-controls__dots').forEach(ol => {
    const fragment = document.createDocumentFragment();

    for (let i = 0; i < count; i++) {
      const li = document.createElement('li');
      li.setAttribute('data-dot-index', String(i + 1));

      const btn = document.createElement('button');
      btn.className = 'slideshow-control button button-unstyled';
      btn.setAttribute('style', `animation-timeline: --slide-${i + 1}`);
      btn.setAttribute('on:click', `/select/${i}`);
      btn.setAttribute('ref', 'dots[]');
      btn.setAttribute('aria-label', `${i + 1} / ${count}`);
      btn.setAttribute('aria-selected', String(i === activeIndex));
      btn.textContent = String(i + 1);

      li.appendChild(btn);
      fragment.appendChild(li);
    }

    ol.replaceChildren(fragment);
  });

  // Actualiza o contador "N / Total" se existir.
  slideshow
    .querySelectorAll('.slideshow-controls__counter')
    .forEach(counter => {
      const slash = counter.querySelector('.slash');
      if (!slash) return;
      let node = slash.nextSibling;
      while (node && node.nodeType !== Node.TEXT_NODE) node = node.nextSibling;
      if (node) node.textContent = String(count);
    });
}

/**
 * Insere a miniatura VTO na 2.ª posição de cada faixa de thumbnails e
 * renumera os atributos de todos os botões.
 *
 * @param {HTMLElement} slideshow
 * @param {string} src
 * @param {number} activeIndex
 */
function rebuildThumbnails(slideshow, src, activeIndex) {
  const count = slideshow.querySelectorAll('slideshow-slide').length;

  slideshow.querySelectorAll('.slideshow-controls__thumbnails').forEach(row => {
    // Remove miniatura VTO anterior, se houver.
    row.querySelectorAll('[data-vto-generated]').forEach(b => b.remove());

    const thumb = document.createElement('button');
    thumb.className =
      'slideshow-control button button-unstyled slideshow-controls__thumbnail';
    thumb.setAttribute('data-vto-generated', 'true');
    thumb.setAttribute('ref', 'thumbnails[]');
    thumb.setAttribute('style', '--aspect-ratio: 1');

    const img = document.createElement('img');
    img.src = src;
    img.alt = '';
    img.loading = 'lazy';
    img.setAttribute('sizes', 'auto, 110, (min-width: 750px) 160');
    thumb.appendChild(img);

    row.insertBefore(thumb, row.children[1] ?? null);

    // Re-numera todos os botões após a inserção.
    row.querySelectorAll('button').forEach((btn, i) => {
      btn.setAttribute('on:click', `/select/${i}`);
      btn.setAttribute('aria-selected', String(i === activeIndex));
      btn.setAttribute('aria-label', `${i + 1} / ${count}`);
    });
  });
}

// =============================================================================
// Init
// =============================================================================

if (!customElements.get('vto-gallery-button')) {
  customElements.define('vto-gallery-button', VtoGalleryButton);
}
